/**
 * Speisekarten-Verwaltung (P2, docs/KONZEPT-SPEISEKARTEN.md) — seit P1 von
 * docs/KONZEPT-KATALOG-NAVIGATION.md (§ 4.2) DIE Kartenansicht: die Kachelwand auf /katalog ist
 * entfallen, /katalog leitet hierher, und die Seitenleiste nennt diesen Ort beim Namen (P0).
 * Karten-Grid mit Art, Zählern, Gültigkeit,
 * aktiv-Toggle; Aktionen: bearbeiten (Name/Art/Gültigkeit), ersetzen (→ Import-Wizard mit
 * vorgewählter Karte), duplizieren (Saison-Workflow), Kategorien zuordnen (Bestand),
 * archivieren (Soft-Delete mit Kaskade + Bestätigung mit Zählern).
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApi, apiMutate } from "../hooks/useApi";
import type { CatalogCategory, MenuCard } from "../ui/types";
import { useContentRefetch } from "../ui/CopilotProvider";
import { VeroeffentlichenDrawer, statusZeile } from "../ui/KarteVeroeffentlichen";
import { Button, Card, Drawer, EmptyState, ErrorNote, Icon, Input, LoadingRows, PageHeader, Select, useBestaetigung, DateInput, Checkbox, ButtonRouterLink } from "../ui/components";
import { TranslationPairReview, type TranslationPair } from "../ui/translation-review";

/*
 * Punkt 81 (Antoine, 20.09.2026): „In meiner Vorstellung gibt es einen Equipment Katalog […]
 * wie die Speisekarten. Also eine eigene Equipment Karte in Karten."
 *
 * Drei neue Kartenarten. Die Namen sind bewusst so gewaehlt, wie der Betrieb spricht —
 * „Equipment-Karte" neben „Speisekarte", nicht „Ressourcen-Karte". Und jede bekommt ihr
 * eigenes Bild: eine Karte, die man am Symbol nicht von der Speisekarte unterscheiden kann,
 * waere in einer Liste von acht Karten wertlos.
 */
const KIND_LABEL: Record<MenuCard["kind"], string> = {
  food: "Speisekarte",
  drinks: "Getränkekarte",
  mixed: "Gemischt",
  equipment: "Equipment-Karte",
  staff: "Personal-Karte",
  other: "Sonstiges",
};
const KIND_ICON: Record<MenuCard["kind"], string> = {
  food: "restaurant",
  drinks: "local_bar",
  mixed: "restaurant_menu",
  equipment: "inventory_2",
  staff: "badge",
  other: "category",
};

function fmtDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString("de-DE");
}
/** Ablauf-Badge: abgelaufen (rot) / läuft in ≤ 14 Tagen ab (gelb). */
function expiryBadge(card: MenuCard): { label: string; tone: "red" | "amber" } | null {
  if (!card.validUntil) return null;
  const until = new Date(card.validUntil);
  if (Number.isNaN(until.getTime())) return null;
  const today = new Date(new Date().toISOString().slice(0, 10));
  if (until < today) return { label: "abgelaufen", tone: "red" };
  const days = Math.round((until.getTime() - today.getTime()) / 86_400_000);
  if (days <= 14) return { label: `läuft in ${days} Tag${days === 1 ? "" : "en"} ab`, tone: "amber" };
  return null;
}

export default function KartenPage() {
  const { bestaetigen, dialog: bestaetigungsDialog } = useBestaetigung();
  const navigate = useNavigate();
  const { data, loading, error, reload } = useApi<MenuCard[]>("/api/menu-cards");
  const catsQ = useApi<CatalogCategory[]>("/api/catalog/categories");
  // Review-Befund 31.07.2026: SECHS Melde-Stellen im Assistenten - die meisten von allen - und
  // kein Zuhoerer. Speisekarten sind der Bereich, in dem der Assistent am meisten tut.
  useContentRefetch("menu_card", () => {
    reload();
    catsQ.reload();
  });
  const [editing, setEditing] = useState<MenuCard | null>(null);
  const [publishFor, setPublishFor] = useState<string | null>(null);
  const [assignFor, setAssignFor] = useState<MenuCard | null>(null);
  const [assignSel, setAssignSel] = useState<Set<string>>(new Set());
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Mehrsprachigkeit: KI-Uebersetzung einer Karte. Die Uebersetzung wird NIE blind
  // geschrieben - der Vorschlag landet im selben Paar-Review wie der PDF-Weg
  // (KONZEPT-MEHRSPRACHIGKEIT, Neufassung), erst die Bestaetigung schreibt.
  const [translateFor, setTranslateFor] = useState<MenuCard | null>(null);
  const [translateLang, setTranslateLang] = useState("en");
  const [translateResult, setTranslateResult] = useState<string | null>(null);
  const [translatePairs, setTranslatePairs] = useState<TranslationPair[] | null>(null);
  const [translateCatPairs, setTranslateCatPairs] = useState<{ categoryId: string; targetName: string; name: string }[]>([]);
  const [translateNotFound, setTranslateNotFound] = useState(0);

  const cards = data ?? [];
  // Solange eine Veroeffentlichung auf die Uebersetzung wartet (nicht „haengt"), alle 10 s neu
  // laden — der Sweep veroeffentlicht im Hintergrund, die Statuszeile soll das zeigen.
  const laeuft = (c: MenuCard) => !c.intern && c.veroeffentlichenAngefordertAt != null && c.uebersetzungStatus !== "haengt";
  const irgendeineLaeuft = cards.some(laeuft);
  useEffect(() => {
    if (!irgendeineLaeuft) return;
    const t = setInterval(reload, 10_000);
    return () => clearInterval(t);
  }, [irgendeineLaeuft, reload]);
  // Bestands-Zuordnung: nur aktive Kategorien OHNE Karte sind zuordenbar.
  const unassignedCats = (catsQ.data ?? []).filter((c) => c.archivedAt == null && (c.cardId ?? null) == null);

  async function patchCard(card: MenuCard, patch: Partial<MenuCard>) {
    try {
      await apiMutate(`/api/menu-cards/${card.id}`, "PATCH", patch);
      setFormError(null);
      reload();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : String(e));
    }
  }

  async function archiveCard(card: MenuCard) {
    const ok = await bestaetigen({
      titel: `Karte „${card.name}“ archivieren?`,
      text: `Damit werden auch ihre ${card.categoryCount ?? 0} ${card.categoryCount === 1 ? "Kategorie" : "Kategorien"} und ${card.itemCount ?? 0} Positionen archiviert (nicht gelöscht – Angebote und Pakete behalten ihre Referenzen).`,
      bestaetigen: "Archivieren",
    });
    if (!ok) return;
    try {
      await apiMutate(`/api/menu-cards/${card.id}`, "DELETE");
      reload();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : String(e));
    }
  }

  async function duplicateCard(card: MenuCard) {
    setBusy(true);
    try {
      await apiMutate(`/api/menu-cards/${card.id}/duplicate`, "POST", {});
      setFormError(null);
      reload();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  /** Schritt 1: KI-Vorschlag holen (schreibt noch NICHTS). */
  async function runTranslate() {
    if (!translateFor) return;
    setBusy(true);
    setTranslateResult(null);
    try {
      const res = await apiMutate<{
        lang: string;
        pairs: { targetId: string; targetName: string; targetPriceCents: number; name: string; description: string | null; confidence: number }[];
        categoryPairs: { categoryId: string; targetName: string; name: string }[];
        totalItems: number;
        notFound: number;
      }>(`/api/menu-cards/${translateFor.id}/translate`, "POST", { lang: translateLang });
      setTranslatePairs(res.pairs.map((p) => ({ ...p, include: true })));
      setTranslateCatPairs(res.categoryPairs);
      setTranslateNotFound(res.notFound);
      setFormError(null);
    } catch (e) {
      setFormError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  /** Schritt 2: nur die bestaetigten Paare auf die Karte schreiben. */
  async function applyTranslate() {
    if (!translateFor || !translatePairs) return;
    const included = translatePairs.filter((p) => p.include);
    setBusy(true);
    try {
      const res = await apiMutate<{ translatedItems: number; translatedCategories: number }>(
        `/api/menu-cards/${translateFor.id}/apply-translation`,
        "POST",
        {
          lang: translateLang,
          pairs: included.map((p) => ({ targetId: p.targetId, name: p.name, description: p.description })),
          categoryPairs: translateCatPairs.map((c) => ({ categoryId: c.categoryId, name: c.name })),
        },
      );
      setTranslatePairs(null);
      setTranslateCatPairs([]);
      setTranslateResult(`${res.translatedItems} Positionen und ${res.translatedCategories} Kategorien übersetzt (${translateLang.toUpperCase()}). Feinschliff: Position im Katalog öffnen.`);
      setFormError(null);
      reload();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function assignCategories() {
    if (!assignFor || assignSel.size === 0) return;
    setBusy(true);
    try {
      await apiMutate(`/api/menu-cards/${assignFor.id}/assign-categories`, "POST", { categoryIds: [...assignSel] });
      setAssignFor(null);
      setAssignSel(new Set());
      setFormError(null);
      reload();
      void catsQ.reload();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {bestaetigungsDialog}
      <PageHeader
        title="Karten"
        subtitle="Karten gruppieren den Katalog und lassen sich einzeln erneuern – die Mittagskarte bleibt unberührt, wenn die Saisonkarte wechselt."
        actions={
          /* P2 (§ 4.3): „Zum Katalog" war ein Weg, kein Handgriff — und fuehrte seit P1 ohnehin
             im Kreis hierher zurueck. Der Import bleibt: er ist laut § 4.1 kein Menuepunkt,
             sondern die Handlung, die von „Karten" ausgeht. */
          <Button onClick={() => navigate("/katalog/import")}>
            <Icon name="upload_file" className="text-[18px]" /> Karte importieren
          </Button>
        }
      />

      {formError && <ErrorNote message={formError} />}

      {loading ? (
        <LoadingRows />
      ) : error ? (
        <ErrorNote message={error} />
      ) : cards.length === 0 ? (
        <EmptyState
          icon="menu_book"
          title="Noch keine Karten"
          hint="Importiere eine Speise- oder Getränkekarte und wähle im Review »Neue Karte anlegen« – oder ordne bestehende Kategorien einer Karte zu."
          cta={{ label: "Karte importieren", icon: "upload_file", onClick: () => navigate("/katalog/import") }}
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {/* grid-cols-1 + Kachel min-w-0: sonst waechst die Spur mobil auf die volle Breite des
              (truncate-)Titels und die Kachel ragt rechts aus dem Bild. */}
          {cards.map((card) => {
            const badge = expiryBadge(card);
            const validity = [fmtDate(card.validFrom), fmtDate(card.validUntil)];
            return (
              <Card key={card.id} className="flex min-w-0 flex-col gap-3 p-5">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex min-w-0 items-start gap-2.5">
                    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-[9px] bg-primary/12 text-primary">
                      <Icon name={KIND_ICON[card.kind]} className="text-[18px]" />
                    </span>
                    <div className="min-w-0">
                      <p className="truncate font-body-lg text-body-lg font-semibold text-on-background dark:text-inverse-on-surface">{card.name}</p>
                      <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                        {KIND_LABEL[card.kind]} · {card.categoryCount ?? 0} {card.categoryCount === 1 ? "Kategorie" : "Kategorien"} · {card.itemCount ?? 0} Positionen
                      </p>
                    </div>
                  </div>
                  {!card.active && <span className="shrink-0 rounded-full bg-surface-container-high px-2 py-0.5 text-label-md dark:bg-white/[0.08]">inaktiv</span>}
                </div>

                {/* Mehrsprachigkeit: Sprach-Status (uebersetzte Positionen je Sprache). */}
                {Object.keys(card.translationCoverage ?? {}).length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5">
                    {Object.entries(card.translationCoverage ?? {}).map(([lang, n]) => (
                      <span
                        key={lang}
                        className={`rounded px-1.5 py-0.5 font-label-md text-[11px] font-semibold ${
                          n >= (card.itemCount ?? 0)
                            ? "bg-secondary/15 text-secondary dark:text-[#b8ae9e]"
                            : "bg-secondary/15 text-secondary dark:text-[#b8ae9e]"
                        }`}
                        title={`${n} von ${card.itemCount ?? 0} Positionen auf ${lang.toUpperCase()} übersetzt`}
                      >
                        {lang.toUpperCase()} {n}/{card.itemCount ?? 0}
                      </span>
                    ))}
                  </div>
                )}

                {(validity[0] || validity[1] || badge) && (
                  <div className="flex flex-wrap items-center gap-2 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                    {(validity[0] || validity[1]) && (
                      <span className="inline-flex items-center gap-1">
                        <Icon name="event" className="text-[15px]" />
                        {validity[0] ?? "…"} – {validity[1] ?? "…"}
                      </span>
                    )}
                    {badge && (
                      <span
                        className={`rounded px-1.5 py-0.5 font-label-md text-[11px] font-semibold ${
                          badge.tone === "red" ? "bg-error/10 text-error" : "bg-secondary/15 text-secondary dark:text-[#b8ae9e]"
                        }`}
                      >
                        {badge.label}
                      </span>
                    )}
                  </div>
                )}

                {!card.intern && (
                  <p
                    className={`font-body-sm text-body-sm ${
                      laeuft(card)
                        ? "self-start rounded px-1.5 py-0.5 bg-primary/10 font-semibold text-primary"
                        : card.uebersetzungStatus === "haengt"
                          ? "self-start rounded px-1.5 py-0.5 bg-secondary/15 text-secondary dark:text-[#b8ae9e]"
                          : "text-secondary dark:text-secondary-fixed-dim"
                    }`}
                  >
                    {laeuft(card) && "Wird veröffentlicht · "}
                    {statusZeile({
                      publishedVersion: card.publishedVersion ?? null,
                      publishedAt: card.publishedAt ?? null,
                      uebersetzung: { status: card.uebersetzungStatus ?? null, versuche: 0, fehler: null },
                      wartetAufUebersetzung: card.veroeffentlichenAngefordertAt != null,
                    })}
                  </p>
                )}

                <div className="mt-auto flex flex-wrap items-center gap-2 border-t border-outline-variant/30 pt-3 dark:border-outline/30">
                  {/* P1 (§ 4.2): je Karte ALLE Handlungen an einem Ort — auch das Öffnen, das
                      vorher nur über die entfallene Kachelwand ging. */}
                  <ButtonRouterLink
                    to={`/katalog?cardId=${card.id}`}
                    variant="secondary" className="flex-1 justify-center"
                    title="Positionen dieser Karte öffnen"
                  >
                    <Icon name="arrow_forward" className="text-[16px]" /> Öffnen
                  </ButtonRouterLink>
                  <Button variant="secondary" className="flex-1 justify-center" onClick={() => setEditing(card)}>
                    <Icon name="edit" className="text-[16px]" /> Bearbeiten
                  </Button>
                  <Button
                    variant="secondary"
                    className="flex-1 justify-center"
                    title="Neue Version dieser Karte importieren – Weggefallenes wird archiviert"
                    onClick={() => navigate(`/katalog/import?cardId=${card.id}&mode=replace`)}
                  >
                    <Icon name="sync_alt" className="text-[16px]" /> Ersetzen
                  </Button>
                  {!card.intern && (
                    <Button
                      variant="secondary"
                      className="flex-1 justify-center"
                      title="Stand dieser Karte für Website und Widget veröffentlichen"
                      onClick={() => setPublishFor(card.id)}
                    >
                      <Icon name="publish" className="text-[16px]" /> Veröffentlichen
                    </Button>
                  )}
                  <button
                    type="button"
                    className="rounded-lg p-2 text-secondary hover:bg-surface-container-high dark:hover:bg-surface-variant/30"
                    title={card.active ? "Deaktivieren (nicht mehr im KI-Vorschlag/Picker)" : "Aktivieren"}
                    onClick={() => void patchCard(card, { active: !card.active })}
                  >
                    <Icon name={card.active ? "toggle_on" : "toggle_off"} />
                  </button>
                  <button
                    type="button"
                    className="rounded-lg p-2 text-secondary hover:bg-surface-container-high dark:hover:bg-surface-variant/30"
                    title="Karte duplizieren (Saison-Workflow)"
                    disabled={busy}
                    onClick={() => void duplicateCard(card)}
                  >
                    <Icon name="content_copy" className="text-[18px]" />
                  </button>
                  <button
                    type="button"
                    className="rounded-lg p-2 text-secondary hover:bg-surface-container-high dark:hover:bg-surface-variant/30"
                    title="KI-Übersetzung der Karte (Namen + Beschreibungen; Preise/Allergene bleiben unberührt)"
                    onClick={() => {
                      setTranslateFor(card);
                      setTranslateResult(null);
                    }}
                  >
                    <Icon name="translate" className="text-[18px]" />
                  </button>
                  {unassignedCats.length > 0 && (
                    <button
                      type="button"
                      className="rounded-lg p-2 text-secondary hover:bg-surface-container-high dark:hover:bg-surface-variant/30"
                      title="Bestehende Kategorien (Ohne Karte) dieser Karte zuordnen"
                      onClick={() => {
                        setAssignFor(card);
                        setAssignSel(new Set());
                      }}
                    >
                      <Icon name="playlist_add" className="text-[18px]" />
                    </button>
                  )}
                  <button
                    type="button"
                    className="rounded-lg p-2 text-on-surface-variant hover:bg-surface-container-high dark:text-secondary-fixed-dim"
                    title="Archivieren (Soft-Delete inkl. Kategorien + Positionen)"
                    onClick={() => void archiveCard(card)}
                  >
                    <Icon name="archive" className="text-[18px]" />
                  </button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* P1 (§ 4.2): die beiden Wege, die sonst mit der Kachelwand verschwunden wären — die
          bewusste Gesamtliste und die Positionen ohne Karten-Zuordnung. Sie stehen bewusst
          UNTER den Karten: die Karte ist die führende Ordnung (§ 9), das hier ist die Ausnahme. */}
      <div className="mt-6 flex flex-wrap gap-3">
        <Card to="/katalog?view=liste" as={Link} className="flex items-center gap-3 px-4 py-3 transition-shadow hover:shadow-md">
          <Icon name="list" className="text-[20px] text-on-surface-variant" />
          <span className="font-body-sm text-body-sm font-medium text-on-background dark:text-inverse-on-surface">
            Alle Positionen als Liste
          </span>
        </Card>
        <Card to="/katalog?cardId=none" as={Link} className="flex items-center gap-3 px-4 py-3 transition-shadow hover:shadow-md">
          <Icon name="category" className="text-[20px] text-on-surface-variant" />
          <span className="font-body-sm text-body-sm font-medium text-on-background dark:text-inverse-on-surface">
            Ohne Karte
          </span>
          <span className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
            Equipment, Personal &amp; Positionen ohne Karten-Zuordnung
          </span>
        </Card>
      </div>

      {/* Wissen-Umbau (05.08.2026): der Hinweis stand auf der entfallenen Kachelwand und zieht mit
          hierher — /katalog/karten ist jetzt die gewohnte Stelle. */}
      <div className="mt-4">
        <Card to="/gedaechtnis?tab=lernt" as={Link} className="group flex items-center gap-3 p-4 transition-shadow hover:shadow-md">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-primary/12 text-primary">
            <Icon name="menu_book" className="text-[18px]" />
          </span>
          <span className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
            Referenzen sind zu <span className="font-semibold text-primary">Wissen → Woraus MAESTRO lernt</span> umgezogen
          </span>
          <Icon name="arrow_forward" className="ml-auto text-[18px] text-primary" />
        </Card>
      </div>

      {/* Bearbeiten: Name, Art, Gültigkeit */}
      {/* Bughunt 01.09.2026 (s. VeranstaltungsDatenDrawer.tsx): jedes Feld speichert einzeln per
          onBlur/onChange, "Fertig" ist ein reiner Schliessen-Knopf — kein Sammel-Speichern, die
          Warnung waere ein Fehlalarm. */}
      <Drawer open={editing != null} onClose={() => setEditing(null)} title={editing ? `Karte – ${editing.name}` : ""} autosave>
        {editing && (
          <div className="space-y-4">
            {formError && <ErrorNote message={formError} />}
            <label className="block">
              <span className="mb-1 block font-body-sm text-body-sm font-semibold">Name</span>
              <Input
                defaultValue={editing.name}
                onBlur={(e) => e.target.value.trim() && e.target.value.trim() !== editing.name && void patchCard(editing, { name: e.target.value.trim() })}
              />
            </label>
            <label className="block">
              <span className="mb-1 block font-body-sm text-body-sm font-semibold">Art</span>
              {/* Design E6: unkontrolliert (`defaultValue`) — `editing` wird nach `patchCard` nicht
                  aktualisiert, ein kontrolliertes Feld spraenge auf den alten Wert zurueck. */}
              <Select
                defaultValue={editing.kind}
                onValueChange={(v) => void patchCard(editing, { kind: v as MenuCard["kind"] })}
                options={[
                  { value: "food", label: "Speisekarte" },
                  { value: "drinks", label: "Getränkekarte" },
                  { value: "mixed", label: "Gemischt (Speisen + Getränke)" },
                  // Punkt 81: die drei Ressourcen-Arten, bewusst unten.
                  { value: "equipment", label: "Equipment-Karte" },
                  { value: "staff", label: "Personal-Karte" },
                  { value: "other", label: "Sonstiges" },
                ]}
              />
              <span className="mt-1 block font-body-sm text-[12px] text-on-surface-variant/70">
                Die Art bestimmt Art und MwSt. NEU importierter Positionen; bestehende bleiben unverändert.
              </span>
            </label>
            {/* Migration 391: struktureller Verwendungszweck-Tag - rein informativ/manuell in
                dieser Iteration (keine automatische Zuordnung zum Ort-Typ einer Veranstaltung,
                das folgt spaeter). KEINE Exklusivitaet: "Beides" ist der Normalfall. */}
            <div className="block">
              <span className="mb-1 block font-body-sm text-body-sm font-semibold">Verwendungszweck</span>
              <div className="flex flex-wrap gap-2">
                {(
                  [
                    { value: "event", label: "Event/Inhouse" },
                    { value: "catering", label: "Catering/Außer Haus" },
                    { value: "both", label: "Beides" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`rounded-full border px-3 py-1.5 font-body-sm text-body-sm ${
                      editing.context === opt.value
                        ? "border-primary bg-primary/10 font-semibold text-primary"
                        : "border-outline-variant/40 text-secondary hover:bg-surface-container-high/40 dark:border-outline/40 dark:text-secondary-fixed-dim"
                    }`}
                    onClick={() => void patchCard(editing, { context: opt.value })}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <span className="mt-1 block font-body-sm text-[12px] text-on-surface-variant/70">
                Nur Anzeige/manuelle Einordnung in dieser Iteration - die automatische Zuordnung zum Ort-Typ eines Vorgangs folgt später.
              </span>
            </div>
            {/* min-w-0: s. VeranstaltungsDatenDrawer.tsx (identisches Zwei-Felder-Muster). */}
            <div className="grid grid-cols-2 gap-3">
              <label className="block min-w-0">
                <span className="mb-1 block font-body-sm text-body-sm font-semibold">Gültig von</span>
                <DateInput
                  defaultValue={editing.validFrom ?? ""}
                  onValueChange={(v) => void patchCard(editing, { validFrom: v || null })}
                />
              </label>
              <label className="block min-w-0">
                <span className="mb-1 block font-body-sm text-body-sm font-semibold">Gültig bis</span>
                <DateInput
                  defaultValue={editing.validUntil ?? ""}
                  onValueChange={(v) => void patchCard(editing, { validUntil: v || null })}
                />
              </label>
            </div>
            <p className="font-body-sm text-[12px] text-on-surface-variant/70">
              Außerhalb des Fensters (bezogen auf das Termindatum) schlägt die KI keine Positionen dieser Karte mehr vor.
            </p>
            <div className="flex justify-end">
              <Button onClick={() => setEditing(null)}>Fertig</Button>
            </div>
          </div>
        )}
      </Drawer>

      {/* Mehrsprachigkeit: KI-Übersetzung — Vorschlag → Paar-Review → Übernehmen */}
      <Drawer
        open={translateFor != null}
        onClose={() => {
          setTranslateFor(null);
          setTranslatePairs(null);
          setTranslateCatPairs([]);
          setTranslateResult(null);
        }}
        title={translateFor ? `KI-Übersetzung – ${translateFor.name}` : ""}
      >
        {translateFor && (
          <div className="space-y-4">
            {formError && <ErrorNote message={formError} />}
            {translatePairs == null ? (
              <>
                <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                  Übersetzt Namen und Beschreibungen aller {translateFor.itemCount ?? 0} Positionen sowie die Kategorie-Titel.
                  Preise, Allergene und Struktur bleiben unangetastet. Etablierte Gerichtenamen (z. B. „Vitello Tonnato") werden nicht übersetzt.
                  Du prüfst jeden Vorschlag, bevor etwas gespeichert wird; bestehende Übersetzungen der gewählten Sprache werden beim Übernehmen überschrieben.
                </p>
                <label className="block">
                  <span className="mb-1 block font-body-sm text-body-sm font-semibold">Zielsprache</span>
                  <Select
                    ariaLabel="Zielsprache"
                    value={translateLang}
                    onValueChange={setTranslateLang}
                    options={[
                      { value: "en", label: "Englisch" },
                      { value: "it", label: "Italienisch" },
                      { value: "fr", label: "Französisch" },
                      { value: "es", label: "Spanisch" },
                    ]}
                  />
                </label>
                {translateResult && (
                  <p className="rounded-lg bg-secondary/10 px-3 py-2 font-body-sm text-body-sm text-secondary dark:text-[#b8ae9e]">{translateResult}</p>
                )}
                <div className="flex justify-end gap-3">
                  <Button variant="secondary" onClick={() => setTranslateFor(null)}>Schließen</Button>
                  <Button disabled={busy} onClick={() => void runTranslate()}>
                    {busy ? "Übersetzt …" : "Übersetzung vorschlagen"}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <TranslationPairReview pairs={translatePairs} lang={translateLang} notFound={translateNotFound} onChange={setTranslatePairs} />
                {translateCatPairs.length > 0 && (
                  <p className="font-body-sm text-[12px] text-on-surface-variant/70">
                    {translateCatPairs.length} Kategorie-Titel werden mitübersetzt ({translateCatPairs.map((c) => `„${c.targetName}" → „${c.name}"`).slice(0, 3).join(", ")}
                    {translateCatPairs.length > 3 ? ", …" : ""}).
                  </p>
                )}
                <div className="flex justify-end gap-3">
                  <Button variant="secondary" disabled={busy} onClick={() => setTranslatePairs(null)}>
                    Zurück
                  </Button>
                  <Button
                    disabled={busy || translatePairs.filter((p) => p.include).length === 0}
                    onClick={() => void applyTranslate()}
                  >
                    {busy ? "Übernimmt …" : `${translatePairs.filter((p) => p.include).length} Übersetzungen übernehmen`}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </Drawer>

      {/* Bestands-Zuordnung: Ohne-Karte-Kategorien → diese Karte */}
      <Drawer open={assignFor != null} onClose={() => setAssignFor(null)} title={assignFor ? `Kategorien zuordnen – ${assignFor.name}` : ""}>
        {assignFor && (
          <div className="space-y-4">
            <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
              Wähle Kategorien ohne Karten-Zuordnung, die zu dieser Karte gehören sollen (inklusive ihrer Positionen).
            </p>
            {unassignedCats.length === 0 ? (
              <p className="rounded-lg border border-dashed border-outline-variant/50 p-4 text-center font-body-sm text-body-sm text-secondary">
                Keine Kategorien ohne Karte vorhanden.
              </p>
            ) : (
              <div className="space-y-1.5">
                {unassignedCats.map((c) => (
                  <label key={c.id} className="flex cursor-pointer items-center gap-2 rounded-lg border border-outline-variant/30 px-3 py-2 font-body-sm text-body-sm dark:border-outline/30">
                    <Checkbox
                      checked={assignSel.has(c.id)}
                      onChange={(e) => {
                        const next = new Set(assignSel);
                        if (e.target.checked) next.add(c.id);
                        else next.delete(c.id);
                        setAssignSel(next);
                      }}
                    />
                    {c.name}
                  </label>
                ))}
              </div>
            )}
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setAssignFor(null)}>Abbrechen</Button>
              <Button disabled={busy || assignSel.size === 0} onClick={() => void assignCategories()}>
                {assignSel.size} zuordnen
              </Button>
            </div>
          </div>
        )}
      </Drawer>

      <VeroeffentlichenDrawer cardId={publishFor} onClose={() => setPublishFor(null)} onChanged={reload} />
    </>
  );
}
